// sections/Team.tsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaLinkedin, FaTwitter, FaGithub, FaPlus, FaTimes } from 'react-icons/fa';
import awahImg from '../assets/images/team/thumbnail_Awah_CU.webp';
import mattImg from '../assets/images/team/Mezer-Headshot-1.jpg';
import KevinImg from '../assets/images/team/kevin.png';
import Hiroshi from '../assets/images/team/hirasho-matsui.png';
import Ryan from '../assets/images/team/matt-scaled.jpg';
import BukImg from '../assets/images/team/bukun.jpg';
import DavImg from '../assets/images/team/Dr. David .jpg';
import KapImg from '../assets/images/team/kaplan.jpg';



const FaPlusIcon = FaPlus as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaTimesIcon = FaTimes as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaLinkedinIcon = FaLinkedin as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaTwitterIcon = FaTwitter as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaGithubIcon = FaGithub as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  social: {
    linkedin?: string;
    twitter?: string;
    github?: string;
  };
  email?: string;
  phone?: string;
  bio?: string;
}

const Team = () => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const teamMembers: TeamMember[] = [
    {
      id: 1,
      name: 'Chidiebere Awah MD PhD',
      role: 'CEO',
      image: awahImg,
      social: { linkedin: 'https://www.linkedin.com/in/chidiebere-uzodinma-awah-7462aa124/', twitter: '#' },
      bio: ''
    },
    {
      id: 2,
      name: 'Matt Mezer',
      role: 'Team Member',
      image: mattImg,
      social: { linkedin: 'https://www.linkedin.com/in/mattmezer/', twitter: '#' },
      email: 'mattmezer@gmail.com',
      phone: '',
      bio: ''
    },
    {
      id: 3,
      name: 'Dr. David .T. Asuzu M.D., Ph.D , M.P.H ',
      role: 'Chief Medical Consultant (Adult Cancers)',
      image: DavImg,
      social: { linkedin: '#', twitter: '#' },
      email: '',
      phone: ''
    },
    {
      id: 4,
      name: 'Kevin Struhl',
      role: 'Advisor',
      image: KevinImg,
      social: { linkedin: 'https://www.linkedin.com/in/kevin-struhl-70914414a/', twitter: '#' },
      email: '',
      phone: ''
    },
    {
      id: 5,
      name: 'Hiroshi Matsui',
      role: 'Partner',
      image: Hiroshi,
      social: { linkedin: '#' },
      email: ''
    },
    
    {
      id: 6,
      name: 'Bukonla Rebecca Obikunle',
      role: 'Partner',
      image: BukImg,
      social: { linkedin: 'https://www.linkedin.com/in/bukonla-rebecca-obikunle', twitter: '#' },
      email: '',
      phone: ''
    },
    {
      id: 7,
      name: 'Ryan Pawell',
      role: 'Advisor',
      image: Ryan,
      social: { linkedin: 'https://www.linkedin.com/in/rpawell/', twitter: '#' },
      email: '',
      bio: ''
    },
    {
      id: 8,
      name: 'Matthew Kaplan',
      role: 'Consultant',
      image: KapImg,
      social: {  },
      email: '',
      bio: ''
    }
  ];

  const openModal = (member: TeamMember) => {
    setSelectedMember(member);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedMember(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <section id="team" className="relative py-20 px-4 md:px-16 overflow-hidden bg-gradient-to-br from-blue-900/95 to-purple-900/95">
      {/* Subtle floating particles */}
      <div className="absolute inset-0 z-0 opacity-10">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-blue-300/30"
            style={{
              width: `${Math.random() * 8 + 4}px`,
              height: `${Math.random() * 8 + 4}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 40 - 20],
              x: [0, Math.random() * 40 - 20],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut'
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto z-10">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-bold text-center text-white mb-4"
        >
          Our Team
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-xl text-center text-blue-100 mb-12 max-w-3xl mx-auto"
        >
          Meet the brilliant minds driving our innovative research forward.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamMembers.map((member) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all border border-white/10"
            >
              <div className="relative">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <motion.button
                  onClick={() => openModal(member)}
                  className="absolute bottom-4 right-4 bg-white/90 p-2 rounded-full shadow-md hover:bg-white transition-colors"
                  aria-label="View details"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaPlusIcon className="text-blue-600" />
                </motion.button>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white">{member.name}</h3>
                <p className="text-blue-300 mb-4">{member.role}</p>
                <div className="flex space-x-4">
                  {member.social.linkedin && (
                    <motion.a 
                      href={member.social.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaLinkedinIcon className="text-blue-200 hover:text-blue-400 text-xl transition-colors" />
                    </motion.a>
                  )}
                  {member.social.twitter && (
                    <motion.a 
                      href={member.social.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTwitterIcon className="text-blue-200 hover:text-blue-300 text-xl transition-colors" />
                    </motion.a>
                  )}
                  {member.social.github && (
                    <motion.a 
                      href={member.social.github} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaGithubIcon className="text-blue-200 hover:text-blue-300 text-xl transition-colors" />
                    </motion.a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal for member details */}
      {selectedMember && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-blue-900/90 to-purple-900/90 rounded-xl max-w-md w-full p-6 relative border border-white/10 shadow-2xl"
          >
            <motion.button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              aria-label="Close modal"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimesIcon className="text-xl" />
            </motion.button>
            
            <div className="flex flex-col md:flex-row gap-6">
              <motion.img 
                src={selectedMember.image} 
                alt={selectedMember.name}
                className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0 border-4 border-white/10 shadow-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              />
              <div>
                <h3 className="text-2xl font-bold text-white">{selectedMember.name}</h3>
                <p className="text-blue-300 mb-2">{selectedMember.role}</p>
                
                {selectedMember.email && (
                  <div className="mb-2">
                    <p className="text-sm text-blue-100">Email</p>
                    <p className="text-white">{selectedMember.email}</p>
                  </div>
                )}
                
                {selectedMember.phone && (
                  <div className="mb-2">
                    <p className="text-sm text-blue-100">Phone</p>
                    <p className="text-white">{selectedMember.phone}</p>
                  </div>
                )}
                
                {selectedMember.bio && (
                  <div className="mt-4">
                    <p className="text-sm text-blue-100">Bio</p>
                    <p className="text-white/90">{selectedMember.bio}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default Team;